import { safeStorageSet, safeStorageRemove } from './safe-storage.js';

const STORAGE_PREFIX = 'premium-pending-request:';

/** Each request owns an immutable key. Concurrent tabs cannot replace another request's recovery record. */
export function premiumRequestRecovery(accountId, operation, validate, messages, identityField) {
    const prefix = `${STORAGE_PREFIX}${accountId}:${operation}:`;
    let activeKey = null;
    let body = null;
    const storage = () => {
        if (typeof accountId !== 'string' || !/^\d+$/.test(accountId)) throw new Error('Missing account');
        return window.localStorage;
    };
    const read = key => {
        // safeStorageGet conflates an absent key with a failed read, which cannot authorize a replacement.
        const raw = storage().getItem(key);
        if (raw === null) return null;
        const record = JSON.parse(raw);
        if (typeof record?.body !== 'string') throw new Error('Invalid pending body');
        const payload = JSON.parse(record.body);
        if (record.version !== 1 || record.accountId !== accountId || !validate(payload) ||
            key !== prefix + payload[identityField]) {
            throw new Error('Invalid pending request');
        }
        return record.body;
    };
    const pending = () => {
        const area = storage();
        const keys = new Set();
        for (let index = 0; index < area.length; index++) {
            const key = area.key(index);
            if (key?.startsWith(prefix)) keys.add(key);
        }
        // Validate every record, including records behind the current request. None has an expiry.
        return [...keys].sort().map(key => [key, read(key)]).filter(([, value]) => value !== null);
    };
    const fail = control => {
        control.recoveryBlocked = true;
        control.error = messages.loadError;
        return false;
    };
    return {
        requestToRetry: null,
        recoveryBlocked: false,

        restorePending() {
            try {
                const records = pending();
                const next = records.find(([key]) => key === activeKey) || records[0];
                const nextBody = next?.[1] ?? null;
                if (nextBody !== body || !this.requestToRetry) {
                    this.requestToRetry = nextBody === null ? null : JSON.parse(nextBody);
                }
                activeKey = next?.[0] ?? null;
                body = nextBody;
                this.recoveryBlocked = false;
                return true;
            } catch {
                return fail(this);
            }
        },

        persistPending(payload) {
            if (!this.restorePending()) return null;
            if (body !== null) return body;
            try {
                if (!validate(payload)) throw new Error('Invalid request');
                const nextBody = JSON.stringify(payload);
                const key = prefix + payload[identityField];
                const record = JSON.stringify({ version: 1, accountId, body: nextBody });
                const existing = read(key);
                if (existing !== null && existing !== nextBody) throw new Error('Request identity collision');
                if (existing === null && !safeStorageSet(key, record)) throw new Error('Request not stored');
                if (read(key) !== nextBody) throw new Error('Request not durable');
                activeKey = key;
                body = nextBody;
                this.requestToRetry = JSON.parse(body);
                return body;
            } catch {
                fail(this);
                return null;
            }
        },

        clearPending(completedBody) {
            try {
                // A late completion removes only its UUID, even after another tab has advanced the queue.
                const completed = JSON.parse(completedBody);
                if (!validate(completed)) throw new Error('Invalid completed request');
                const key = prefix + completed[identityField];
                const saved = read(key);
                if (saved !== null && saved !== completedBody) throw new Error('Completed request changed');
                if (saved !== null && (!safeStorageRemove(key) || read(key) !== null)) {
                    throw new Error('Pending request not cleared');
                }
                if (activeKey === key) {
                    activeKey = null;
                    body = null;
                    this.requestToRetry = null;
                }
                return this.restorePending();
            } catch {
                return fail(this);
            }
        },
    };
}
